// Buyer Q&A (ADR-0039): /rooms/:roomId/questions… One endpoint family for every
// surface — the API decides from the caller's role in the room what each sees:
//   - a buyer: only their own questions, only RELEASED answers;
//   - the bound seller: only questions routed to them, no buyer identity;
//   - the room admin: the whole queue, the asking buyer, drafts and the stage.
// In a BUYER-RUN room (admin_role 'buyer', mode 'buy_side') it runs the other way:
// the admin and the buy-side team ask, every buy-side reader sees every question
// (with who asked), the bound seller answers directly and sees only the buyer firm.
// Question and answer text travel in request bodies, never in a URL.
import { ApiError, api } from './client';

/** Longest question a buyer may ask (server QUESTION_MAX_CHARS). */
export const QUESTION_MAX_CHARS = 4000;
/** Longest answer (server ANSWER_MAX_CHARS). */
export const ANSWER_MAX_CHARS = 10000;
/** Most documents one answer may link (server ANSWER_MAX_DOCUMENTS). */
export const ANSWER_MAX_DOCUMENTS = 25;

export type QuestionStatus = 'open' | 'answered' | 'closed';
/** The admin queue stage (admin only). */
export type QuestionStage = 'new' | 'with_seller' | 'draft_ready' | 'answered' | 'closed';
export type QuestionViewerRole = 'admin' | 'seller' | 'buyer';
/** sell_side: buyers ask the deal team. buy_side (buyer-run room): the buy side asks the seller. */
export type QaMode = 'sell_side' | 'buy_side';

/** A linked document as the caller sees it (only documents the caller may see are listed). */
export interface QuestionDocumentRef {
  id: string;
  name: string | null;
}

export interface QuestionAnswerView {
  body: string;
  documents: QuestionDocumentRef[];
  answeredAt: string | null;
  /** Always true for a buyer (a buyer never receives a draft). */
  released: boolean;
  /** Admin/seller only: who wrote it, in which capacity. */
  authorRole?: 'intermediary' | 'seller' | null;
  /**
   * Admin only, on an unreleased draft: linked documents the asking buyer can't
   * see. Releasing is refused until they are removed — or released without them.
   */
  notVisibleToBuyer?: QuestionDocumentRef[];
}

export interface QuestionView {
  id: string;
  body: string;
  status: QuestionStatus;
  createdAt: string;
  /** The document the buyer pointed at, when the caller can see it. */
  document: QuestionDocumentRef | null;
  /** The folder the buyer pointed at, when the caller can see it. */
  category: { id: string; name: string } | null;
  answer: QuestionAnswerView | null;
  /** Admin/seller only. */
  routedToSeller?: boolean;
  /** Admin only. */
  stage?: QuestionStage;
  /** Admin only (sell-side rooms): the published Buyer Questions copy, or null. */
  published?: PublishedQuestionView | null;
  /** Admin only — and every buy-side reader in a buyer-run room: who asked. */
  buyer?: { actorId: string; email: string | null; displayName: string | null };
  /** The seller in a buyer-run room only: the buyer firm that asked (never the individual). */
  askedByFirm?: string | null;
}

export interface ListQuestionsResponse {
  role: QuestionViewerRole;
  /** Absent from older servers / demo mocks = sell_side. */
  mode?: QaMode;
  questions: QuestionView[];
}

export interface AskQuestionRequest {
  body: string;
  documentId?: string | null;
  categoryTagId?: string | null;
}

export interface AnswerQuestionRequest {
  body: string;
  documentIds: string[];
}

/** A document the AI found that might answer a question (never answer text). */
export interface QuestionSuggestion {
  id: string;
  name: string;
  categoryPath: string[];
}

export interface QuestionSuggestionsResponse {
  documents: QuestionSuggestion[];
  /** The server's fixed message when the question is not a find request. */
  message: string | null;
}

/**
 * A published Buyer Question (ADR-0039 §"Buyer Questions"): the wording the room
 * admin published and the linked documents the READER can see — never who asked.
 */
export interface PublishedQuestionView {
  id: string;
  question: string;
  answer: { body: string; documents: QuestionDocumentRef[] };
  publishedAt: string;
}

export interface BuyerQuestionsResponse {
  /** False for a buyer who can't see the CIM yet (and in buyer-run rooms): keep the area hidden. */
  available: boolean;
  questions: PublishedQuestionView[];
}

/** The wording to publish; omit either to keep the default (the original / the current published text). */
export interface PublishQuestionRequest {
  question?: string;
  answer?: string;
  /** On an edit: also replace the shared links with the answer's current links (else the snapshot is kept). */
  refreshLinks?: boolean;
}

/** The cache key every surface uses for a room's question list. */
export function questionsQueryKey(roomId: string) {
  return ['rooms', roomId, 'questions'] as const;
}

/** The cache key for a room's published Buyer Questions. */
export function buyerQuestionsQueryKey(roomId: string) {
  return ['rooms', roomId, 'buyer-questions'] as const;
}

const base = (roomId: string) => `/rooms/${roomId}/questions`;

/** Buyer Questions: admin and bound seller always; a buyer once they can see the CIM. */
export function listBuyerQuestions(roomId: string): Promise<BuyerQuestionsResponse> {
  return api.get<BuyerQuestionsResponse>(`/rooms/${roomId}/buyer-questions`);
}

/** Admin only (sell-side rooms): add an answered question to Buyer Questions, or edit its published wording. */
export function publishQuestion(
  roomId: string,
  questionId: string,
  body: PublishQuestionRequest = {},
): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/publish`, body);
}

/** Admin only: take a question out of Buyer Questions. */
export function unpublishQuestion(roomId: string, questionId: string): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/unpublish`);
}

export function listQuestions(roomId: string): Promise<ListQuestionsResponse> {
  return api.get<ListQuestionsResponse>(base(roomId));
}

/** A buyer (sell-side room); the admin or a buy-side team member (buyer-run room). */
export function askQuestion(roomId: string, body: AskQuestionRequest): Promise<QuestionView> {
  return api.post<QuestionView>(base(roomId), body);
}

/** Admin only: send an open question to the room's seller. */
export function routeQuestion(roomId: string, questionId: string): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/route`);
}

/** Admin (released at once) or the routed seller (a draft for the admin to release). */
export function answerQuestion(
  roomId: string,
  questionId: string,
  body: AnswerQuestionRequest,
): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/answer`, body);
}

/**
 * Admin only: release the seller's draft to the buyer. A draft linking documents
 * the buyer can't see is refused (400 `document_not_visible_to_buyer`) unless
 * `dropNotVisible` — then it is released without those documents.
 */
export function releaseAnswer(
  roomId: string,
  questionId: string,
  options: { dropNotVisible?: boolean } = {},
): Promise<QuestionView> {
  return api.post<QuestionView>(
    `${base(roomId)}/${questionId}/release`,
    options.dropNotVisible ? { dropNotVisible: true } : undefined,
  );
}

/** Admin only. */
export function closeQuestion(roomId: string, questionId: string): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/close`);
}

/** Admin only. */
export function reopenQuestion(roomId: string, questionId: string): Promise<QuestionView> {
  return api.post<QuestionView>(`${base(roomId)}/${questionId}/reopen`);
}

/** Admin or the routed seller: documents that might answer the question. */
export function fetchQuestionSuggestions(
  roomId: string,
  questionId: string,
): Promise<QuestionSuggestionsResponse> {
  return api.get<QuestionSuggestionsResponse>(`${base(roomId)}/${questionId}/suggestions`);
}

/** The ids an ADMIN's answer or release was refused for (400 `document_not_visible_to_buyer`), else []. */
export function refusedDocumentIds(error: unknown): string[] {
  if (!(error instanceof ApiError)) return [];
  const problem = error.problem;
  if (problem?.code !== 'document_not_visible_to_buyer') return [];
  return Array.isArray(problem.documentIds) ? problem.documentIds : [];
}

/** What the buyer and the admin call each status. */
export const QUESTION_STATUS_LABELS: Record<QuestionStatus, string> = {
  open: 'Open',
  answered: 'Answered',
  closed: 'Closed',
};
